import { useCallback, useEffect, useState } from 'react'
import type { PlayCoordinates } from '@/modules/companies/constants/companies-domain'

const CACHE_KEY = 'partiu-quadra-user-location'
const MILLISECONDS_PER_SECOND = 1_000
const CACHE_TTL_MS = 10 * 60 * MILLISECONDS_PER_SECOND
const GEOLOCATION_TIMEOUT_MS = 10_000
const LOCATION_EVENT_NAME = 'partiu-quadra-location-updated'
const GEOLOCALITY_LANGUAGE = 'pt'

type UserLocationErrorCode = 'permission_denied' | 'unavailable' | 'timeout' | 'unsupported'
type UserLocationStatus = 'idle' | 'loading' | 'success' | 'error'

interface UserLocationCache {
  coordinates: PlayCoordinates
  city?: string
  cachedAt: number
}

export interface UserLocationError {
  code: UserLocationErrorCode
  message: string
}

export interface UseUserLocationResult {
  status: UserLocationStatus
  coordinates: PlayCoordinates | null
  city: string | null
  error: UserLocationError | null
  requestLocation: (force?: boolean) => void
  clearCachedLocation: () => void
}

function readCache(): UserLocationCache | null {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = window.sessionStorage.getItem(CACHE_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as UserLocationCache
    const isValid = Number.isFinite(parsed.cachedAt)
    if (!isValid || Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(CACHE_KEY)
      return null
    }

    return parsed
  } catch {
    window.sessionStorage.removeItem(CACHE_KEY)
    return null
  }
}

function writeCache(data: UserLocationCache) {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(data))
  window.dispatchEvent(new Event(LOCATION_EVENT_NAME))
}

function mapGeoError(error: GeolocationPositionError): UserLocationError {
  if (error.code === error.PERMISSION_DENIED) {
    return {
      code: 'permission_denied',
      message: 'Permissão negada. Você pode informar cidade ou CEP manualmente.',
    }
  }

  if (error.code === error.TIMEOUT) {
    return {
      code: 'timeout',
      message: 'Tempo esgotado ao buscar sua localização. Tente novamente.',
    }
  }

  return {
    code: 'unavailable',
    message: 'Não foi possível obter sua localização no momento.',
  }
}

async function resolveCityName(coordinates: PlayCoordinates): Promise<string | null> {
  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coordinates.latitude}&longitude=${coordinates.longitude}&localityLanguage=${GEOLOCALITY_LANGUAGE}`,
    )

    if (!response.ok) {
      return null
    }

    const payload = (await response.json()) as {
      city?: string
      locality?: string
      principalSubdivision?: string
    }

    return payload.city ?? payload.locality ?? payload.principalSubdivision ?? null
  } catch {
    return null
  }
}

export function useUserLocation(): UseUserLocationResult {
  const [status, setStatus] = useState<UserLocationStatus>('idle')
  const [coordinates, setCoordinates] = useState<PlayCoordinates | null>(null)
  const [city, setCity] = useState<string | null>(null)
  const [error, setError] = useState<UserLocationError | null>(null)

  useEffect(() => {
    const syncFromCache = () => {
      const cached = readCache()
      if (!cached) {
        return
      }

      setCoordinates(cached.coordinates)
      setCity(cached.city ?? null)
      setStatus('success')
    }

    syncFromCache()
    window.addEventListener(LOCATION_EVENT_NAME, syncFromCache)
    return () => window.removeEventListener(LOCATION_EVENT_NAME, syncFromCache)
  }, [])

  const requestLocation = useCallback((force = false) => {
    if (typeof window === 'undefined' || !window.navigator.geolocation) {
      setStatus('error')
      setError({
        code: 'unsupported',
        message: 'Este navegador não suporta geolocalização.',
      })
      return
    }

    if (!force) {
      const cached = readCache()
      if (cached) {
        setCoordinates(cached.coordinates)
        setCity(cached.city ?? null)
        setStatus('success')
        setError(null)
        return
      }
    }

    setStatus('loading')
    setError(null)

    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextCoordinates: PlayCoordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }
        setCoordinates(nextCoordinates)
        setCity(null)
        setStatus('success')
        writeCache({
          coordinates: nextCoordinates,
          cachedAt: Date.now(),
        })

        void resolveCityName(nextCoordinates).then((resolvedCity) => {
          if (!resolvedCity) {
            return
          }

          setCity(resolvedCity)
          writeCache({
            coordinates: nextCoordinates,
            city: resolvedCity,
            cachedAt: Date.now(),
          })
        })
      },
      (geoError) => {
        setStatus('error')
        setError(mapGeoError(geoError))
      },
      {
        enableHighAccuracy: true,
        timeout: GEOLOCATION_TIMEOUT_MS,
        maximumAge: 0,
      },
    )
  }, [])

  const clearCachedLocation = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(CACHE_KEY)
      window.dispatchEvent(new Event(LOCATION_EVENT_NAME))
    }
    setCoordinates(null)
    setCity(null)
    setStatus('idle')
    setError(null)
  }, [])

  return {
    status,
    coordinates,
    city,
    error,
    requestLocation,
    clearCachedLocation,
  }
}
